import { Widget } from '@/components/widget';
import { useListener } from '@/hooks/use-listener';
import { useEffect, useState, useRef } from 'react';
import { createElement } from 'react';
import { LoadingScreenSpinner } from '@/components/loading-screen-spinner';
import {
  loadedScriptsCache,
  pause,
} from '@/features/components-studio/helpers';
import { nanoid } from 'nanoid';

interface ComponentPreviewWidgetProps {
  id?: string;
  defaultConfig?: ComponentConfig;
}

interface ComponentConfig {
  scriptUrl: string;
  componentName: string;
  title?: string;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 5000;
const REGISTRY_CHECK_DELAY = 100;

export function ComponentPreviewWidget({
  id,
  defaultConfig,
}: ComponentPreviewWidgetProps) {
  const [componentConfig, setComponentConfig] = useState<ComponentConfig>(
    defaultConfig || {
      scriptUrl: '',
      componentName: '',
      title: 'Component Preview',
    }
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(1);
  const isScriptLoading = useRef(false);
  const retryTimeoutRef = useRef<number>();
  const mountedRef = useRef(true);
  const widgetId = id || nanoid();

  useListener({
    id: widgetId,
    listenerFn: (data) => {
      // Clear any pending retry timeout
      if (retryTimeoutRef.current) {
        window.clearTimeout(retryTimeoutRef.current);
      }
      setComponentConfig({
        scriptUrl: data.scriptUrl,
        componentName: data.componentName,
      });
      setRetryCount(1);
      setError(null);
      isScriptLoading.current = false;
    },
    type: 'preview/component',
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!componentConfig.scriptUrl || isScriptLoading.current) {
      return;
    }

    // Don't attempt to load if we've reached max retries
    if (retryCount > MAX_RETRIES) {
      return;
    }

    const loadScript = async () => {
      if (isScriptLoading.current || !mountedRef.current) {
        return;
      }

      isScriptLoading.current = true;
      setIsLoading(true);
      setError(null);
      let script: HTMLScriptElement | null = null;

      try {
        // Check if script is already loaded
        if (loadedScriptsCache.has(componentConfig.scriptUrl)) {
          // Wait a bit for the component to be registered
          await pause(REGISTRY_CHECK_DELAY);
          if (!customElements.get(componentConfig.componentName)) {
            throw new Error(
              `Component ${componentConfig.componentName} not found in custom elements registry`
            );
          }
          setIsLoading(false);
          setRetryCount(1);
          return;
        }

        script = document.createElement('script');
        script.src = componentConfig.scriptUrl;

        // Create a promise to handle script load/error
        const scriptLoadPromise = new Promise((resolve, reject) => {
          script!.onload = async () => {
            try {
              // Wait a bit for the component to be registered
              await pause(REGISTRY_CHECK_DELAY);
              if (!customElements.get(componentConfig.componentName)) {
                reject(
                  new Error(
                    `Component ${componentConfig.componentName} not found in custom elements registry`
                  )
                );
                return;
              }
              loadedScriptsCache.add(componentConfig.scriptUrl);
              resolve(true);
            } catch (err) {
              reject(err);
            }
          };
          script!.onerror = () => {
            reject(new Error('Failed to load script'));
          };
        });

        document.head.appendChild(script);
        await scriptLoadPromise;
        if (mountedRef.current) {
          setIsLoading(false);
          setRetryCount(1);
        }
      } catch (err) {
        if (script) {
          document.head.removeChild(script);
        }

        if (!mountedRef.current) {
          return;
        }

        // Only set error and stop loading if we've reached max retries
        if (retryCount >= MAX_RETRIES) {
          setError(
            err instanceof Error ? err.message : 'Failed to load script'
          );
          setIsLoading(false);
        } else {
          // Clear any existing timeout before setting a new one
          if (retryTimeoutRef.current) {
            window.clearTimeout(retryTimeoutRef.current);
          }
          retryTimeoutRef.current = window.setTimeout(() => {
            if (mountedRef.current) {
              setRetryCount((prev) => prev + 1);
            }
          }, RETRY_DELAY);
        }
      } finally {
        isScriptLoading.current = false;
      }
    };

    loadScript();

    return () => {
      // Clear any pending retry timeout
      if (retryTimeoutRef.current) {
        window.clearTimeout(retryTimeoutRef.current);
      }
      const script = document.querySelector(
        `script[src="${componentConfig.scriptUrl}"]`
      );
      if (script) {
        document.head.removeChild(script);
      }
      isScriptLoading.current = false;
    };
  }, [componentConfig.scriptUrl, retryCount]);

  return (
    <Widget title={componentConfig.title} id={widgetId} key={widgetId}>
      {isLoading ? (
        <div className="flex flex-col justify-center items-center h-32">
          <LoadingScreenSpinner />
        </div>
      ) : error ? (
        <div className="text-red-500 text-center p-4">{error}</div>
      ) : componentConfig.componentName ? (
        createElement(componentConfig.componentName)
      ) : null}
    </Widget>
  );
}
